import CredentialGroupOverviewTab from 'js/components/credentials/cards/CredentialGroupOverviewTab';
import CredentialOverviewTabContainer from 'js/components/credentials/cards/CredentialOverviewTabContainer';
import DataMapCardOverviewTabContainer from 'js/components/data_maps/cards/DataMapCardOverviewTabContainer';
import DataSetRAGDetail from 'js/components/data_sets/cards/DataSetRAGDetail';
import ReusableTxOverviewTabContainer from 'js/components/reusableTransforms/cards/ReusableTxOverviewTabContainer';
import AclTabViewContainer from 'js/components/elements/aclView/AclTabViewContainer';
import {
  ACL_RESOURCE_TYPE_CREDENTIALS,
  ACL_RESOURCE_TYPE_LOOKUPS,
  ACL_RESOURCE_TYPE_TRANSFORM,
} from 'js/constants/aclConstants';
import { CONNECTION_CATEGORY_DATABASE } from 'js/constants/connectionCategories';
import {
  RESOURCE_TYPE_ATTR_TRANSFORM,
  RESOURCE_TYPE_CREDENTIALS,
  RESOURCE_TYPE_CREDENTIAL_GROUP,
  RESOURCE_TYPE_DATA_MAP,
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_RECORD_TRANSFORM,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import { isEmpty } from 'lodash';
import { createCredentialTabConfigs } from 'new/components/DetailViewTabs/Credential/config/credential.tabs';
import flowTypes from 'new/components/flows/types/flowTypes';
import { TransformType } from 'new/components/ReusableTransform/types';
import ResourceActivity from 'new/elements/support/detailTabs/ResourceActivity';
import useEntities from 'new/state/entities/repository/useEntities';
import usePaging from 'new/state/paging/repository/usePaging';
import React from 'react';
import { useSelector } from 'react-redux';
import { getAllowedNexsetTabs, NEXSET_TAB_IDS, TAB_METADATA } from 'new/utils/nexsetTabs';
import { dataSinkTabs, getNexsetTabComponent, getSourceTabs } from 'new/utils/resourceTabs';

const getNexsetTabs = ({ data, flowType, getMetaInfoById, dataSource, parentDataSet }) => {
  const allowedTabIds = getAllowedNexsetTabs({
    data,
    flowType,
    getMetaInfoById,
    dataSource,
    parentDataSet,
  });

  return allowedTabIds.map((tabId) => {
    const metadata = TAB_METADATA[tabId];
    return {
      tabName: metadata.label,
      tabElement: getNexsetTabComponent(tabId, data),
    };
  });
};

const getCredentialsTabs = (data) => {
  const tabs = createCredentialTabConfigs();

  const tabsWithProps = tabs.map((tab) => {
    if (tab.value === 'usage') {
      return {
        ...tab,
        tabElement: React.cloneElement(tab.tabElement, {
          credentialId: data.id,
        }),
      };
    }
    return tab;
  });

  return tabsWithProps;
};

const dataMapTabs = [
  {
    tabName: 'Overview',
    tabElement: <DataMapCardOverviewTabContainer />,
  },
  {
    tabName: 'Access',
    tabElement: <AclTabViewContainer resourceType={ACL_RESOURCE_TYPE_LOOKUPS} />,
  },
];

const getCodeContainerTabs = (data) => {
  const detailViewTabs = [
    {
      tabName: 'Overview',
      tabElement: <ReusableTxOverviewTabContainer />,
    },
  ];

  if (data) {
    const resourceType =
      data.output_type === TransformType.ATTRIBUTE
        ? RESOURCE_TYPE_ATTR_TRANSFORM
        : RESOURCE_TYPE_RECORD_TRANSFORM;

    detailViewTabs.push({
      tabName: 'Activity',
      tabElement: <ResourceActivity resourceType={resourceType} data={{}} />,
    });
  }

  detailViewTabs.push({
    tabName: 'Access',
    tabElement: <AclTabViewContainer resourceType={ACL_RESOURCE_TYPE_TRANSFORM} />,
  });
  return detailViewTabs;
};

const getCredentialGroupTabs = (data) => {
  return [
    {
      tabName: 'Overview',
      tabElement: <CredentialGroupOverviewTab data={data} />,
    },
  ];
};

export const useResourceHeaderTabs = (data, { flow, resourceType = undefined }) => {
  const { getMetaInfoById } = usePaging();
  const { data: sources } = useEntities('sources');
  const vendors = useSelector((state: any) => state.entities?.vendors ?? {});
  const parentDataSet = useSelector(
    (state: any) => state.entities.dataSets[data?.parent_data_set_id],
  );
  if (!data) return null;
  const dataSource = sources[flow?.data_source_id];
  const flowType = data?.flow_type || flow?.flow_type;

  if (resourceType) {
    switch (resourceType) {
      case RESOURCE_TYPE_SOURCE:
        return getSourceTabs(data, getMetaInfoById, vendors);

      case RESOURCE_TYPE_SINK:
        return dataSinkTabs(data);

      case RESOURCE_TYPE_DATA_SET:
        return getNexsetTabs({
          data,
          flowType,
          getMetaInfoById,
          dataSource,
          parentDataSet,
        });

      case RESOURCE_TYPE_CREDENTIALS:
        return getCredentialsTabs(data);

      case RESOURCE_TYPE_DATA_MAP:
        return dataMapTabs;

      case RESOURCE_TYPE_RECORD_TRANSFORM:
      case RESOURCE_TYPE_ATTR_TRANSFORM:
        return getCodeContainerTabs(data);
    }
  }

  if (data.type) {
    switch (data.type) {
      case 'SOURCE':
        return getSourceTabs(data, getMetaInfoById, vendors);

      case 'DATA_SINK':
        return dataSinkTabs(data);

      case 'DATA_SET':
        return getNexsetTabs({
          data,
          flowType,
          getMetaInfoById,
          dataSource,
          parentDataSet,
        });

      case RESOURCE_TYPE_CREDENTIAL_GROUP:
        return getCredentialGroupTabs(data);
    }
  }

  switch (data.resourceType) {
    case RESOURCE_TYPE_CREDENTIALS:
      return getCredentialsTabs(data);

    case RESOURCE_TYPE_DATA_MAP:
      return dataMapTabs;

    case RESOURCE_TYPE_RECORD_TRANSFORM:
    case RESOURCE_TYPE_ATTR_TRANSFORM:
      return getCodeContainerTabs(data);

    default:
      return [];
  }
};
